import { forwardRef, useCallback, useState } from 'react'
import { m, LazyMotion, domAnimation, useAnimation } from 'framer-motion'
import Image from 'next/image'
import classnames from 'classnames'

import TalentDashboardImage from '../../../assets/pngs/talent-dashboard.png'
import { OlbyGraphics } from '../../../assets/icons'

import styles from './index.module.scss'

// Animations

const TITLE_ANIMATION = {
  start: { opacity: 0.01, y: '50%' },
  end: {
    y: '0%',
    opacity: 1,
    transition: { type: 'spring', bounce: 0 },
  },
}

const DESCRIPTION_ANIMATION = {
  start: { opacity: 0 },
  end: {
    opacity: 1,
    transition: { type: 'spring', bounce: 0 },
  },
}

const TEXT_ANIMATION = {
  start: { opacity: 0 },
  end: {
    opacity: 1,
    transition: { type: 'spring', bounce: 0, delay: 0.2 },
  },
}

const TITLE_CONTAINER_ANIMATION = {
  start: {
    y: '50%',
  },
  end: {
    y: '0%',
    transition: { type: 'spring', bounce: 0 },
  },
}

const SCROLL_INTO_VIEW = {
  start: { y: '50%', opacity: 0 },
  end: {
    y: '0%',
    opacity: 1,
    transition: { type: 'spring', bounce: 0 },
  },
}

export const ForTalentTransparencyUnfolded = forwardRef<HTMLDivElement>((_, ref) => {
  const [wasAnimationTriggered, setWasAnimationTriggered] = useState(false)

  const headingControl = useAnimation()
  const descriptionControl = useAnimation()
  const textControl = useAnimation()
  const headingContainerControl = useAnimation()

  const onViewportEnter = useCallback(() => {
    if (wasAnimationTriggered) {
      return
    }
    setWasAnimationTriggered(true)

    headingControl.start('end').then(() => {
      descriptionControl.start('end')
      textControl.start('end')
      headingContainerControl.start('end')
    })
  }, [
    wasAnimationTriggered,
    headingControl,
    descriptionControl,
    textControl,
    headingContainerControl,
  ])

  return (
    <div className={styles['container']} ref={ref}>
      <LazyMotion features={domAnimation} strict>
        <m.section
          className={styles['content']}
          viewport={{ once: true, amount: 0.3 }}
          initial="start"
          onViewportEnter={onViewportEnter}
        >
          <m.div
            variants={TITLE_CONTAINER_ANIMATION}
            initial="start"
            animate={headingContainerControl}
          >
            <m.h2
              className={styles['title']}
              initial="start"
              variants={TITLE_ANIMATION}
              animate={headingControl}
            >
              <span className={styles['highlight']}>Transparency</span> unfolded
            </m.h2>
            <m.p
              className={styles['description']}
              animate={descriptionControl}
              variants={DESCRIPTION_ANIMATION}
              initial="start"
            >
              Olby's all-in-one platform has everything you need to focus on what truly matters -
              your career!
            </m.p>
            <m.p
              className={styles['text']}
              animate={textControl}
              variants={TEXT_ANIMATION}
              initial="start"
            >
              We are committed to transparency. Our dashboard gives you a complete overview of your
              experience, allow you to track upcoming payments, and automated invoice generation
              integrated with your bank account.
            </m.p>
          </m.div>
          <m.div
            className={classnames(styles['image-wrapper'], styles['desktop'])}
            variants={SCROLL_INTO_VIEW}
            initial="start"
            whileInView="end"
            viewport={{ once: true, amount: 0.1 }}
          >
            <Image
              src={TalentDashboardImage}
              alt="Olby talent app dashboard"
              sizes="100vw"
              width="0"
              height="0"
              className={styles['image']}
            />
          </m.div>
          <div className={classnames(styles['image-wrapper'], styles['mobile'])}>
            <Image
              src={TalentDashboardImage}
              alt="Olby talent app dashboard"
              width="585"
              height="313"
              className={styles['image']}
            />
          </div>
        </m.section>
        <m.div
          className={styles['bottom-graphics']}
          variants={SCROLL_INTO_VIEW}
          initial="start"
          whileInView="end"
          viewport={{ once: true, amount: 0, margin: '300px' }}
        >
          <OlbyGraphics className={styles['graphics']} />
        </m.div>
      </LazyMotion>
    </div>
  )
})

export default ForTalentTransparencyUnfolded
